import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import OnboardingModal from '../components/OnboardingModal';
import { 
  Leaf, Plus, LogOut, ChevronRight, Calendar, 
  Moon, Zap, Shield, Brain, Heart, Scale, Utensils, 
  Bone, Sparkles, Smile, Loader2, User, Activity,
  Bell, BarChart3, Clock, BookOpen, LineChart, Trophy, Video,
  Pill, Trash2, CalendarCheck, X, Coffee, Sun, Sunset, 
  AlertTriangle, Download, Eye, Pencil, Save, MessageSquare, RefreshCw, Search, Flame, Calculator,
  ArrowRight, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DailyCheckInWidget, DailyTipWidget, WellnessJournalWidget, ReAssessmentPromptWidget, MyStackCard } from '../components/EngagementWidgets';
import MissedYesterdayBanner from '../components/MissedYesterdayBanner';
import WeekBackfillStrip from '../components/WeekBackfillStrip';
import TrackingHub from '../components/TrackingHub';
import TopInsightCard from '../components/TopInsightCard';
import RestockAlertBanner from '../components/RestockAlertBanner';
import QuickRecheckModal from '../components/QuickRecheckModal';import SlotCompletionSparkline from '../components/SlotCompletionSparkline';
import InProgressWidget from '../components/InProgressWidget';
import { ProgressSnapshotWidget } from '../components/ProgressSnapshotCard';
import { MilestonesWidget } from '../components/MilestoneCelebration';
import { todayLocal } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAVED_ASSESSMENT_KEY = 'vitala_saved_assessment';

const categoryIcons = {
  sleep: Moon,
  energy: Zap,
  immunity: Shield,
  stress: Brain,
  weight: Scale,
  heart: Heart,
  digestion: Utensils,
  joints: Bone,
  hair_skin: Sparkles,
  brain_focus: Brain,
  mood: Smile
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, loading: authLoading } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickViewSchedule, setQuickViewSchedule] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasSavedAssessment, setHasSavedAssessment] = useState(false);
  const [moreToolsOpen, setMoreToolsOpen] = useState(false);
  const [streakStats, setStreakStats] = useState(null); // { current_streak, consistency_30d }
  const [topInsight, setTopInsight] = useState(null); // best per-supplement correlation
  const [restockAlerts, setRestockAlerts] = useState([]); // supplements <7 days from empty
  const [medDrift, setMedDrift] = useState(null); // { has_drift, added_meds, removed_meds }
  const [showRecheckModal, setShowRecheckModal] = useState(false);
  const [driftDataForModal, setDriftDataForModal] = useState(null);

  // Check for saved assessment
  useEffect(() => {
    const savedData = localStorage.getItem(SAVED_ASSESSMENT_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const savedTime = new Date(parsed.savedAt);
        const now = new Date();
        const daysDiff = (now - savedTime) / (1000 * 60 * 60 * 24);
        setHasSavedAssessment(daysDiff < 7);
      } catch (e) {
        setHasSavedAssessment(false);
      }
    }
  }, []);

  // Check if first-time user and show onboarding
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('vitala_onboarding_seen');
    if (!hasSeenOnboarding && !authLoading && user) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [authLoading, user]);

  const handleCloseOnboarding = () => {
    localStorage.setItem('vitala_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Handle the nightly wrap-up nudge — either via ?wrap_up_took_all=1 (opened
  // from a push notification with no client running) or via a WRAP_UP_TOOK_ALL
  // postMessage from the service worker (client already open). Both paths POST
  // /api/checkin with all active reminder supplements for today.
  useEffect(() => {
    if (!user) return;

    const logTookAllForToday = async (supplementNames) => {
      try {
        let names = supplementNames;
        if (!Array.isArray(names) || names.length === 0) {
          // Fallback: fetch active reminders and derive names
          const remRes = await axios.get(`${API}/reminders`, { headers: getAuthHeader() });
          const reminders = Array.isArray(remRes.data) ? remRes.data : [];
          const seen = new Set();
          names = [];
          reminders.forEach((r) => {
            if (r.enabled === false || r.is_medication) return;
            const raw = (r.supplement_name || '').trim();
            if (!raw || raw.startsWith('💧') || /water \(cup/i.test(raw)) return;
            const key = raw.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            names.push(raw);
          });
        }
        if (names.length === 0) return;
        const today = todayLocal();
        await axios.post(
          `${API}/checkin`,
          { supplements_taken: names, date: today },
          { headers: getAuthHeader() }
        );
        // Persistent toast with an Undo action so the user knows what just
        // happened and can revert if it wasn't them (e.g. accidental push tap).
        toast.success(
          `Logged ${names.length} supplement${names.length === 1 ? '' : 's'} for today (from nightly nudge)`,
          {
            duration: 15000,
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  await axios.delete(
                    `${API}/checkin/day/${today}`,
                    { headers: getAuthHeader() }
                  );
                  toast.success('Undone — check-in cleared');
                } catch (e) {
                  toast.error('Could not undo — try again');
                }
              },
            },
          }
        );
      } catch (err) {
        console.error('Wrap-up log failed:', err);
      }
    };

    // Path 1: opened from a push notification while app was closed
    const params = new URLSearchParams(window.location.search);
    if (params.get('wrap_up_took_all') === '1') {
      logTookAllForToday(null);
      // Strip the query param so a refresh doesn't re-fire
      params.delete('wrap_up_took_all');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // Path 2: SW postMessage fires while app is already open
    const handleSwMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== 'WRAP_UP_TOOK_ALL') return;
      logTookAllForToday(msg.supplements || null);
    };
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
  }, [user, getAuthHeader]);

  // Fetch streak, top correlation insight, and restock alerts — all needed by the
  // merged plan hero card + supporting home cards. Silent failures OK since each
  // card hides itself if the data is missing.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [streakRes, corrRes, stackRes, driftRes] = await Promise.all([
          axios.get(`${API}/checkin/streak`, { headers: getAuthHeader() }).catch(() => null),
          axios.get(`${API}/wellness/correlation`, { headers: getAuthHeader() }).catch(() => null),
          axios.get(`${API}/stack`, { headers: getAuthHeader() }).catch(() => null),
          axios.get(`${API}/assessment/med-drift`, { headers: getAuthHeader() }).catch(() => null),
        ]);
        if (cancelled) return;
        if (streakRes?.data) {
          setStreakStats({
            current_streak: streakRes.data.current_streak || 0,
            consistency_30d: streakRes.data.consistency_30d || 0,
          });
        }
        const perSupp = corrRes?.data?.per_supplement;
        if (Array.isArray(perSupp) && perSupp.length > 0) {
          setTopInsight(perSupp[0]);
        }
        const alerts = stackRes?.data?.restock_alerts;
        if (Array.isArray(alerts)) {
          const urgent = alerts.filter(a => (a?.days_until_restock ?? 999) <= 7);
          setRestockAlerts(urgent);
        }
        if (driftRes?.data) {
          setMedDrift(driftRes.data);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user, getAuthHeader]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingSchedule) {
          setEditingSchedule(null);
        } else if (quickViewSchedule) {
          setQuickViewSchedule(null);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [quickViewSchedule, editingSchedule]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const [assessmentsRes, schedulesRes] = await Promise.all([
          axios.get(`${API}/assessments`, { headers: getAuthHeader() }),
          axios.get(`${API}/schedules`, { headers: getAuthHeader() })
        ]);
        setAssessments(assessmentsRes.data.assessments || []);
        setSavedSchedules(schedulesRes.data.schedules || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this saved protocol?')) return;
    
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, {
        headers: getAuthHeader()
      });
      setSavedSchedules(prev => prev.filter(s => s.id !== scheduleId));
      toast.success('Protocol deleted');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete protocol');
    }
  };

  const handleDeleteAssessment = async (e, assessmentId) => {
    e.stopPropagation(); // Prevent navigation to results page
    
    if (!window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/assessment/${assessmentId}`, {
        headers: getAuthHeader()
      });
      setAssessments(prev => prev.filter(a => a.id !== assessmentId));
      toast.success('Assessment deleted successfully');
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setEditName(schedule.name);
    // Initialize notes from existing personal_notes in supplements
    const notes = {};
    ['morning', 'afternoon', 'evening', 'bedtime'].forEach(slot => {
      (schedule.schedule?.[slot] || []).forEach((supp, idx) => {
        if (supp.personal_note) {
          notes[`${slot}-${idx}`] = supp.personal_note;
        }
      });
    });
    setEditNotes(notes);
  };

  const handleSaveEdit = async () => {
    if (!editingSchedule) return;
    
    setSavingEdit(true);
    try {
      // Build updated schedule with personal notes
      const updatedSchedule = { ...editingSchedule.schedule };
      ['morning', 'afternoon', 'evening', 'bedtime'].forEach(slot => {
        if (updatedSchedule[slot]) {
          updatedSchedule[slot] = updatedSchedule[slot].map((supp, idx) => ({
            ...supp,
            personal_note: editNotes[`${slot}-${idx}`] || null
          }));
        }
      });

      const response = await axios.put(
        `${API}/schedules/${editingSchedule.id}`,
        { name: editName, schedule: updatedSchedule },
        { headers: getAuthHeader() }
      );

      // Update local state
      setSavedSchedules(prev => prev.map(s => 
        s.id === editingSchedule.id ? response.data.schedule : s
      ));
      
      setEditingSchedule(null);
      toast.success('Protocol updated!');
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update protocol');
    } finally {
      setSavingEdit(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTopCategories = (healthScores) => {
    return Object.entries(healthScores)
      .sort(([, a], [, b]) => a - b)
      .slice(0, 3)
      .map(([cat]) => cat);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="patient-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A]"
          >
            <Logo size="default" linkTo={null} className="pointer-events-none" />
          </button>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(assessments.length > 0 ? '/quiz?prefill=true' : '/quiz')}
              className="btn-primary hidden sm:flex"
              data-testid="header-new-assessment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {assessments.length > 0 ? 'Update Assessment' : 'New Assessment'}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#F5F5F0] hover:bg-[#E5E5E0] transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-[#1A1A1A] hidden sm:inline">
                    {user?.name?.split(' ')[0]}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-[#1A1A1A]">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/ask')}>
                  <Brain className="w-4 h-4 mr-2" />
                  Ask AI
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/journal')}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Supplement Journal
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/symptoms')}>
                  <Activity className="w-4 h-4 mr-2" />
                  Track Symptoms
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/reminders')}>
                  <Bell className="w-4 h-4 mr-2" />
                  Reminders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/compare')}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Compare Progress
                </DropdownMenuItem>
                {assessments.length > 0 && (
                  <DropdownMenuItem onClick={() => navigate('/edit-answers')} data-testid="menu-edit-answers">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit My Answers
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/supplements')}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Browse Supplements
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Revise Answers Reminder - Show if last assessment is older than 3 months */}
        {assessments.length > 0 && (() => {
          const lastAssessment = assessments[0];
          const lastDate = new Date(lastAssessment.created_at);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          
          if (lastDate < threeMonthsAgo) {
            return (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200"
              >
                <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <CalendarCheck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">Time to update your answers?</p>
                      <p className="text-sm text-blue-700">
                        Your last assessment was {Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24 * 30))} months ago. 
                        We recommend updating every 3 months for current recommendations.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/quiz?prefill=true')}
                    size="sm"
                    className="btn-primary whitespace-nowrap"
                  >
                    Update My Answers
                  </Button>
                </div>
              </motion.div>
            );
          }
          return null;
        })()}

        {/* Condition-Specific Guide Banners */}
        {assessments.length > 0 && (() => {
          const latest = assessments[0];
          const answers = latest.answers || [];
          const conditionAnswer = answers.find(a => a.question_id === 'health_conditions');
          const conditions = conditionAnswer ? 
            (Array.isArray(conditionAnswer.answer) ? conditionAnswer.answer : [String(conditionAnswer.answer)]) : [];
          
          const guides = [
            { match: 'Multiple Sclerosis', path: '/ms-guide', label: 'MS Quick Start Guide', desc: 'Access your personalized MS supplement protocol, DMT compatibility guide, and lifestyle strategies.', color: 'purple' },
            { match: 'Thyroid disorder', path: '/thyroid-guide', label: 'Thyroid Support Guide', desc: 'Evidence-based thyroid supplement protocol with medication timing guide for Hashimoto\'s and Graves\'.', color: 'emerald' },
            { match: 'PCOS', path: '/pcos-guide', label: 'PCOS Support Guide', desc: 'Targeted supplement protocol for insulin resistance, hormonal balance, and inflammation management.', color: 'pink' },
            { match: 'Arthritis', path: '/arthritis-guide', label: 'Arthritis Support Guide', desc: 'Evidence-based joint health protocol for OA and RA with medication compatibility and lifestyle strategies.', color: 'cyan' },
            { match: 'Diabetes', path: '/diabetes-guide', label: 'Diabetes & Blood Sugar Guide', desc: 'Targeted supplement protocol for insulin sensitivity, glucose control, and complication prevention.', color: 'blue' },
            { match: 'IBS', path: '/ibs-guide', label: 'IBS & Gut Health Guide', desc: 'Gut healing protocol with probiotics, anti-spasmodics, and microbiome restoration for all IBS subtypes.', color: 'green' },
          ];

          const matched = guides.filter(g => conditions.some(c => c.includes(g.match)));
          if (matched.length === 0) return null;

          const colorMap = {
            purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600', title: 'text-purple-900', desc: 'text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700' },
            emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-600', title: 'text-emerald-900', desc: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
            pink: { bg: 'bg-pink-50', border: 'border-pink-200', icon: 'bg-pink-100 text-pink-600', title: 'text-pink-900', desc: 'text-pink-700', btn: 'bg-pink-600 hover:bg-pink-700' },
            cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'bg-cyan-100 text-cyan-600', title: 'text-cyan-900', desc: 'text-cyan-700', btn: 'bg-cyan-600 hover:bg-cyan-700' },
            blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600', title: 'text-blue-900', desc: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' },
            green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600', title: 'text-green-900', desc: 'text-green-700', btn: 'bg-green-600 hover:bg-green-700' },
          };

          return matched.map(guide => {
            const c = colorMap[guide.color];
            return (
              <motion.div
                key={guide.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-4 p-4 rounded-xl ${c.bg} border ${c.border}`}
                data-testid={`guide-banner-${guide.color}`}
              >
                <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full ${c.icon} flex items-center justify-center flex-shrink-0`}>
                      <BookOpen className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${c.title}`}>{guide.label}</p>
                      <p className={`text-sm ${c.desc}`}>{guide.desc}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(guide.path)}
                    size="sm"
                    className={`${c.btn} text-white whitespace-nowrap`}
                  >
                    View Guide
                  </Button>
                </div>
              </motion.div>
            );
          });
        })()}

        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            Track your health journey and view your personalized recommendations.
          </p>
        </motion.div>

        {/* In-progress drafts (quiz / edit-answers) — surfaces unfinished work first */}
        <div className="mb-4">
          <InProgressWidget userId={user?.id} />
        </div>

        {/* Hero Tile — conditional by assessment state */}
        {(() => {
          const hasAssessment = assessments.length > 0;
          if (!hasAssessment) {
            return (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                onClick={() => navigate('/quiz')}
                className="w-full mb-8 p-7 sm:p-8 rounded-2xl bg-gradient-to-br from-primary to-primary/85 text-white text-left hover:from-primary hover:to-primary/95 active:scale-[0.99] transition-all group shadow-lg shadow-primary/20"
                data-testid="hero-tile-start-assessment"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wider text-white/70 mb-1.5">
                      Get Started
                    </p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1.5 leading-tight">
                      Start Your Free Assessment
                    </h2>
                    <p className="text-sm text-white/85 leading-relaxed">
                      34 questions · ~5 min · Get your personalized supplement plan with safety checks
                    </p>
                    <div className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white">
                      Take the quiz
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          }

          // Returning user: show recent results card
          const latest = assessments[0];
          // Match the Assessment History card's "plan" logic exactly so the
          // hero count never disagrees with the history row. Source of truth:
          //   - "Supplements" = the user's stack (current_supplements), or fall
          //     back to the AI-recommended set if they haven't logged a stack.
          //   - "Medications" = the separate `medications` field captured at
          //     assessment time — NOT items inside recommended_supplements.
          const __norm = (s) => (s || '').toLowerCase()
            .replace(/\s*\([^)]*\)/g, '')
            .replace(/\s+\d+\s*(mg|mcg|g|iu)\b.*$/, '')
            .replace(/\s+/g, ' ')
            .trim();
          const __dedupe = (arr) => {
            const seen = new Set();
            return arr.filter(s => {
              const n = __norm(typeof s === 'string' ? s : (s?.name || ''));
              if (!n || seen.has(n)) return false;
              seen.add(n);
              return true;
            });
          };
          const __curSupps = latest.current_supplements || [];
          const __recSupps = (latest.recommended_supplements || []).filter(s => !s.is_medication);
          const suppCount = __curSupps.length > 0
            ? __dedupe(__curSupps).length
            : __dedupe(__recSupps).length;
          const medCount = (latest.medications || []).length;
          const daysSince = Math.floor((Date.now() - new Date(latest.created_at).getTime()) / (1000 * 60 * 60 * 24));
          const interactionCount = (latest.drug_interactions || []).length;

          // Safety pill: 3 states
          //   1. Has medications → interaction check was performed → green ✓ or amber ⚠
          //   2. No medications → nothing to check, hide the pill
          //   3. Medication drift detected → override to amber "Re-check needed"
          let safetyPill = null;
          if (medCount > 0) {
            if (interactionCount === 0) {
              // If the user did a quick re-check recently, surface that in the pill
              // so returning users can see freshness at a glance.
              let subLine = `Checked against your ${medCount} medication${medCount !== 1 ? 's' : ''} on ${formatDate(latest.created_at)}`;
              if (latest.last_recheck_at) {
                const recheckDays = Math.floor((Date.now() - new Date(latest.last_recheck_at).getTime()) / (1000 * 60 * 60 * 24));
                if (recheckDays === 0) subLine = `Re-checked today · ${medCount} medication${medCount !== 1 ? 's' : ''}`;
                else if (recheckDays === 1) subLine = `Re-checked yesterday · ${medCount} medication${medCount !== 1 ? 's' : ''}`;
                else if (recheckDays <= 30) subLine = `Re-checked ${recheckDays} days ago · ${medCount} medication${medCount !== 1 ? 's' : ''}`;
              }
              safetyPill = {
                tone: 'green',
                icon: 'shield-check',
                title: 'No interactions flagged',
                sub: subLine,
              };
            } else {
              safetyPill = {
                tone: 'amber',
                icon: 'shield-alert',
                title: `${interactionCount} potential interaction${interactionCount !== 1 ? 's' : ''} flagged`,
                sub: 'Tap to review — discuss with your provider',
              };
            }
            // Freshness warning if interaction check is >90 days old and user
            // has meds. Suppress this nag when the user has done a quick
            // re-check recently (<=30 days) so the pill doesn't say both
            // "Re-checked 3 days ago" AND "Consider a re-check" at once.
            const hasRecentRecheck = !!(latest.last_recheck_at &&
              Math.floor((Date.now() - new Date(latest.last_recheck_at).getTime()) / (1000 * 60 * 60 * 24)) <= 30);
            if (daysSince > 90 && !hasRecentRecheck) {
              safetyPill.stale = true;
              safetyPill.sub = `${safetyPill.sub} · Consider a re-check (${daysSince} days old)`;
            }
          }

          // Med-drift override: if user has added/removed meds in reminders since
          // their last assessment, the stored interaction check is out of date.
          // This wins over the base pill (safety-first) and gives an actionable
          // "Re-check now" CTA.
          if (medDrift?.has_drift) {
            const addedNames = (medDrift.added_meds || []).map(m => m.name).filter(Boolean);
            const removedNames = (medDrift.removed_meds || []).map(m => m.name).filter(Boolean);
            const parts = [];
            if (addedNames.length > 0) {
              parts.push(`added ${addedNames.slice(0, 2).join(', ')}${addedNames.length > 2 ? ` +${addedNames.length - 2}` : ''}`);
            }
            if (removedNames.length > 0) {
              parts.push(`removed ${removedNames.slice(0, 2).join(', ')}${removedNames.length > 2 ? ` +${removedNames.length - 2}` : ''}`);
            }
            safetyPill = {
              tone: 'amber',
              icon: 'shield-alert',
              title: 'Re-check recommended',
              sub: `You've ${parts.join(' and ')} since your last check · Retake to re-scan interactions`,
              action: 're-check',
            };
          }

          // Freshness signal
          let freshness;
          if (daysSince < 90) {
            freshness = { tone: 'green', dot: 'bg-emerald-500', text: `Updated ${daysSince === 0 ? 'today' : daysSince === 1 ? 'yesterday' : `${daysSince} days ago`} — looking fresh` };
          } else if (daysSince < 180) {
            freshness = { tone: 'amber', dot: 'bg-amber-500', text: `${daysSince} days ago — consider updating` };
          } else {
            freshness = { tone: 'red', dot: 'bg-red-500', text: `${daysSince} days ago — time to refresh your plan` };
          }

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              role="button"
              tabIndex={0}
              onClick={() => navigate('/my-plan')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/my-plan');
                }
              }}
              className="w-full mb-8 p-6 sm:p-7 rounded-2xl bg-white border border-border/60 text-left hover:border-primary/40 hover:shadow-md transition-all group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="hero-tile-recent-results"
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wider text-primary/70 mb-1.5">
                    Your Plan
                  </p>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] mb-1.5 leading-tight">
                    Your Recent Results
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="font-semibold text-[#1A1A1A]">{suppCount} supplement{suppCount !== 1 ? 's' : ''}</span>
                    {medCount > 0 && (
                      <>
                        {' · '}
                        <span className="font-semibold text-red-600">{medCount} medication{medCount !== 1 ? 's' : ''}</span>
                      </>
                    )}
                    {' · '}
                    {formatDate(latest.created_at)}
                  </p>

                  {/* Streak + consistency chips — only if streak data has arrived */}
                  {streakStats && (streakStats.current_streak > 0 || streakStats.consistency_30d > 0) && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="hero-streak-chips">
                      {streakStats.current_streak > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100">
                          🔥 {streakStats.current_streak}-day streak
                        </span>
                      )}
                      {streakStats.consistency_30d > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                          {streakStats.consistency_30d}% · 30d
                        </span>
                      )}
                    </div>
                  )}

                  {/* Safety pill — inline reassurance next to med count.
                      When in drift-override state, becomes tappable → opens quick re-check modal. */}
                  {safetyPill && (() => {
                    const isActionable = safetyPill.action === 're-check';
                    const commonClass = `flex items-start gap-2 mt-3 p-2.5 rounded-xl text-left w-full ${
                      safetyPill.tone === 'green'
                        ? 'bg-emerald-50 border border-emerald-100'
                        : 'bg-amber-50 border border-amber-100'
                    } ${isActionable ? 'hover:bg-amber-100 transition-colors cursor-pointer' : ''}`;
                    const inner = (
                      <>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          safetyPill.tone === 'green' ? 'bg-emerald-100' : 'bg-amber-100'
                        }`}>
                          {safetyPill.icon === 'shield-check' ? (
                            <svg className={`w-3.5 h-3.5 ${safetyPill.tone === 'green' ? 'text-emerald-600' : 'text-amber-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                          ) : (
                            <svg className={`w-3.5 h-3.5 ${safetyPill.tone === 'green' ? 'text-emerald-600' : 'text-amber-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-semibold leading-tight flex items-center gap-1.5 ${
                            safetyPill.tone === 'green' ? 'text-emerald-800' : 'text-amber-800'
                          }`}>
                            {safetyPill.title}
                            {isActionable && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200/70 text-amber-900">
                                Tap to fix
                              </span>
                            )}
                          </p>
                          <p className={`text-[11px] leading-snug mt-0.5 ${
                            safetyPill.tone === 'green' ? 'text-emerald-700/80' : 'text-amber-700/80'
                          }`}>
                            {safetyPill.sub}
                          </p>
                        </div>
                      </>
                    );
                    return isActionable ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDriftDataForModal(medDrift);
                          setShowRecheckModal(true);
                        }}
                        className={commonClass}
                        data-testid="hero-safety-pill"
                      >
                        {inner}
                      </button>
                    ) : (
                      <div className={commonClass} data-testid="hero-safety-pill">
                        {inner}
                      </div>
                    );
                  })()}

                  <div className="flex items-center gap-1.5 mt-3" data-testid="assessment-freshness">
                    <span className={`w-2 h-2 rounded-full ${freshness.dot}`} />
                    <span className="text-xs text-muted-foreground">{freshness.text}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all flex-shrink-0 mt-1 hidden sm:block" />
              </div>
            </motion.div>
          );
        })()}

        {/* Restock alerts (only if any supplement is <7 days from empty) + Top correlation insight */}
        {(restockAlerts.length > 0 || topInsight) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <RestockAlertBanner
              alerts={restockAlerts}
              onClick={() => navigate('/my-stack')}
            />
            <TopInsightCard
              insight={topInsight}
              onClick={() => navigate('/progress')}
            />
          </div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6"
        >
          <button
            onClick={() => navigate(assessments.length > 0 ? '/quiz?prefill=true' : '/quiz')}
            className="p-5 rounded-2xl bg-primary text-white text-left hover:bg-primary/90 transition-colors group"
            data-testid="dashboard-new-assessment-btn"
          >
            <Plus className="w-7 h-7 mb-3" />
            <h3 className="text-base font-semibold mb-1">{assessments.length > 0 ? 'Update Assessment' : 'New Assessment'}</h3>
            <p className="text-white/80 text-xs">{assessments.length > 0 ? 'Refresh your plan' : 'Take a health quiz'}</p>
          </button>

          <button
            onClick={() => navigate('/reminders')}
            className="p-5 rounded-2xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
            data-testid="dashboard-reminders-btn"
          >
            <Bell className="w-7 h-7 mb-3 text-amber-500" />
            <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Reminders</h3>
            <p className="text-muted-foreground text-xs">Set alerts</p>
          </button>

          <button
            onClick={() => navigate('/symptoms')}
            className="p-5 rounded-2xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
            data-testid="dashboard-symptoms-btn"
          >
            <Activity className="w-7 h-7 mb-3 text-green-600" />
            <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Symptoms</h3>
            <p className="text-muted-foreground text-xs">Track wellness</p>
          </button>

          <button
            onClick={() => navigate('/calculator')}
            className="p-5 rounded-2xl bg-white border border-border/50 text-left hover:border-amber-300 transition-colors group"
            data-testid="dashboard-calculator-btn"
          >
            <Calculator className="w-7 h-7 mb-3 text-amber-600" />
            <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Calculators</h3>
            <p className="text-muted-foreground text-xs">Calories & macros</p>
          </button>

          <button
            onClick={() => navigate('/ask')}
            className="p-5 rounded-2xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
            data-testid="ask-ai-btn"
          >
            <Brain className="w-7 h-7 mb-3 text-purple-500" />
            <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Ask AI</h3>
            <p className="text-muted-foreground text-xs">Supplement Q&A</p>
          </button>

          <button
            onClick={() => navigate('/compare')}
            className="p-5 rounded-2xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
            data-testid="dashboard-compare-btn"
          >
            <BarChart3 className="w-7 h-7 mb-3 text-blue-500" />
            <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Compare</h3>
            <p className="text-muted-foreground text-xs">Track progress</p>
          </button>
        </motion.div>

        {/* More tools — secondary tier */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-8"
          data-testid="dashboard-more-tools"
        >
          <button
            onClick={() => setMoreToolsOpen(v => !v)}
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-primary flex items-center gap-1.5 mb-3"
            data-testid="more-tools-toggle"
          >
            {moreToolsOpen ? '− Hide more tools' : '+ Show more tools'}
          </button>
          {moreToolsOpen && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="more-tools-grid">
              <button
                onClick={() => navigate('/supplements')}
                className="p-3.5 rounded-xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
              >
                <Sparkles className="w-5 h-5 mb-2 text-secondary" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Browse</h3>
                <p className="text-muted-foreground text-[10px]">All supplements</p>
              </button>
              <button
                onClick={() => navigate('/journal')}
                className="p-3.5 rounded-xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
              >
                <BookOpen className="w-5 h-5 mb-2 text-purple-600" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Journal</h3>
                <p className="text-muted-foreground text-[10px]">Log supplements</p>
              </button>
              <button
                onClick={() => navigate('/lookup')}
                className="p-3.5 rounded-xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
                data-testid="lookup-btn"
              >
                <Search className="w-5 h-5 mb-2 text-blue-500" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Lookup</h3>
                <p className="text-muted-foreground text-[10px]">Identify items</p>
              </button>
              <button
                onClick={() => navigate('/analytics')}
                className="p-3.5 rounded-xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
              >
                <LineChart className="w-5 h-5 mb-2 text-indigo-600" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Analytics</h3>
                <p className="text-muted-foreground text-[10px]">View insights</p>
              </button>
              <button
                onClick={() => navigate('/telehealth')}
                className="p-3.5 rounded-xl bg-white border border-border/50 text-left hover:border-primary/30 transition-colors group"
              >
                <Video className="w-5 h-5 mb-2 text-teal-500" />
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Telehealth</h3>
                <p className="text-muted-foreground text-[10px]">Consult online</p>
              </button>
            </div>
          )}
        </motion.div>

        {/* ── Engagement Section: Check-In, Tip, Progress, Community ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-8"
          data-testid="engagement-section"
        >
          <h2 className="text-xl font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-amber-500" />
            Your Daily Wellness
          </h2>
          
          {/* Re-Assessment Prompt (90+ days) */}
          {assessments.length > 0 && (
            <div className="mb-4">
              <ReAssessmentPromptWidget
                getAuthHeader={getAuthHeader}
                latestAssessmentDate={assessments[0]?.created_at}
                assessmentCount={assessments.length}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left column: Tracking hub (Today/Week/Trends) + Tip */}
            <div className="space-y-4">
              <MissedYesterdayBanner getAuthHeader={getAuthHeader} />
              <TrackingHub
                getAuthHeader={getAuthHeader}
                supplements={assessments.length > 0 ? (assessments[0].recommended_supplements || []) : []}
              />
              <DailyTipWidget getAuthHeader={getAuthHeader} />
            </div>
            {/* Right column: My Stack + Quick access to Progress */}
            <div className="space-y-4">
              <MyStackCard getAuthHeader={getAuthHeader} />
              <button
                onClick={() => navigate('/progress')}
                className="w-full p-4 rounded-2xl bg-white border border-border/60 hover:border-primary/40 hover:shadow-sm transition-all text-left group"
                data-testid="dashboard-view-progress-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-[#1A1A1A]">View your progress</p>
                    <p className="text-xs text-muted-foreground">Milestones · community · assessment history</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-primary transition-all" />
                </div>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Saved Schedules */}
        {savedSchedules.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="mb-12"
          >
            <h2 className="text-xl font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-primary" />
              My Saved Protocols
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedSchedules.slice(0, 6).map((schedule, index) => (
                <motion.div
                  key={schedule.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                  className="p-4 bg-white rounded-xl border border-border/50 hover:border-primary/30 transition-all group cursor-pointer"
                  onClick={() => setQuickViewSchedule(schedule)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Pill className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#1A1A1A] text-sm line-clamp-1">
                          {schedule.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(schedule.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-colors">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(schedule);
                        }}
                        className="p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        title="Edit protocol"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSchedule(schedule.id);
                        }}
                        className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete protocol"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {schedule.supplement_count} supplements
                      </span>
                      {schedule.medications?.length > 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {schedule.medications.length} meds
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickViewSchedule(schedule);
                        }}
                        className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Quick View
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </main>

      {/* Quick View Protocol Modal */}
      {quickViewSchedule && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setQuickViewSchedule(null);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1A1A1A]">{quickViewSchedule.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    Saved on {formatDate(quickViewSchedule.created_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setQuickViewSchedule(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Current Medications */}
              {quickViewSchedule.medications?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Medications</h3>
                  <div className="flex flex-wrap gap-2">
                    {quickViewSchedule.medications.map((med, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {med}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Time Slots */}
              <div className="space-y-4">
                {/* Morning */}
                {quickViewSchedule.schedule?.morning?.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Coffee className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-amber-900">Morning</h3>
                      <span className="text-xs text-amber-600">With breakfast</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {quickViewSchedule.schedule.morning.map((supp, idx) => (
                        <div key={idx} className="p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-amber-600" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A1A1A] truncate">{supp.name}</p>
                              <p className="text-xs text-muted-foreground">{supp.dosage}</p>
                            </div>
                            {supp.note && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {supp.note}
                              </span>
                            )}
                          </div>
                          {supp.personal_note && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-100/50 rounded p-1.5">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="italic">{supp.personal_note}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Afternoon */}
                {quickViewSchedule.schedule?.afternoon?.length > 0 && (
                  <div className="p-4 bg-sky-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Sun className="w-5 h-5 text-sky-600" />
                      <h3 className="font-semibold text-sky-900">Afternoon</h3>
                      <span className="text-xs text-sky-600">With lunch</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {quickViewSchedule.schedule.afternoon.map((supp, idx) => (
                        <div key={idx} className="p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-sky-600" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A1A1A] truncate">{supp.name}</p>
                              <p className="text-xs text-muted-foreground">{supp.dosage}</p>
                            </div>
                            {supp.note && (
                              <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {supp.note}
                              </span>
                            )}
                          </div>
                          {supp.personal_note && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-sky-700 bg-sky-100/50 rounded p-1.5">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="italic">{supp.personal_note}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evening */}
                {quickViewSchedule.schedule?.evening?.length > 0 && (
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Sunset className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-orange-900">Evening</h3>
                      <span className="text-xs text-orange-600">With dinner</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {quickViewSchedule.schedule.evening.map((supp, idx) => (
                        <div key={idx} className="p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-orange-600" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A1A1A] truncate">{supp.name}</p>
                              <p className="text-xs text-muted-foreground">{supp.dosage}</p>
                            </div>
                            {supp.note && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {supp.note}
                              </span>
                            )}
                          </div>
                          {supp.personal_note && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-orange-700 bg-orange-100/50 rounded p-1.5">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="italic">{supp.personal_note}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bedtime */}
                {quickViewSchedule.schedule?.bedtime?.length > 0 && (
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Moon className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-indigo-900">Bedtime</h3>
                      <span className="text-xs text-indigo-600">30-60 minutes before sleep</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {quickViewSchedule.schedule.bedtime.map((supp, idx) => (
                        <div key={idx} className="p-2 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-indigo-600" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A1A1A] truncate">{supp.name}</p>
                              <p className="text-xs text-muted-foreground">{supp.dosage}</p>
                            </div>
                            {supp.note && (
                              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {supp.note}
                              </span>
                            )}
                          </div>
                          {supp.personal_note && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-indigo-700 bg-indigo-100/50 rounded p-1.5">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="italic">{supp.personal_note}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Alerts */}
                {quickViewSchedule.schedule?.alerts?.length > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-amber-900">Important Timing Notes</h3>
                    </div>
                    <ul className="space-y-2">
                      {quickViewSchedule.schedule.alerts.map((alert, idx) => (
                        <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          <span>{alert}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border/50 flex items-center justify-between bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setQuickViewSchedule(null);
                  navigate(`/results/${quickViewSchedule.assessment_id}`);
                }}
              >
                View Full Results
              </Button>
              <Button
                onClick={() => navigate('/reminders')}
                className="btn-primary"
              >
                <Bell className="w-4 h-4 mr-2" />
                Set Reminders
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Protocol Modal */}
      {editingSchedule && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditingSchedule(null);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Pencil className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1A1A1A]">Edit Protocol</h2>
                  <p className="text-sm text-muted-foreground">
                    Rename and add personal notes
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEditingSchedule(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Protocol Name */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Protocol Name
                </label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter schedule name..."
                  className="w-full"
                />
              </div>

              {/* Supplement Notes */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-3">
                  Personal Notes for Supplements
                </label>
                <p className="text-xs text-muted-foreground mb-4">
                  Add reminders, experiences, or any notes for each supplement
                </p>

                <div className="space-y-4">
                  {['morning', 'afternoon', 'evening', 'bedtime'].map(slot => {
                    const supplements = editingSchedule.schedule?.[slot] || [];
                    if (supplements.length === 0) return null;
                    
                    const slotColors = {
                      morning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: Coffee, iconColor: 'text-amber-600' },
                      afternoon: { bg: 'bg-sky-50', border: 'border-sky-200', icon: Sun, iconColor: 'text-sky-600' },
                      evening: { bg: 'bg-orange-50', border: 'border-orange-200', icon: Sunset, iconColor: 'text-orange-600' },
                      bedtime: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: Moon, iconColor: 'text-indigo-600' }
                    };
                    const colors = slotColors[slot];
                    const SlotIcon = colors.icon;
                    
                    return (
                      <div key={slot} className={`p-4 ${colors.bg} rounded-xl border ${colors.border}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <SlotIcon className={`w-4 h-4 ${colors.iconColor}`} />
                          <h4 className="font-medium text-[#1A1A1A] capitalize">{slot}</h4>
                        </div>
                        <div className="space-y-3">
                          {supplements.map((supp, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Pill className={`w-4 h-4 ${colors.iconColor}`} />
                                <span className="font-medium text-sm">{supp.name}</span>
                                <span className="text-xs text-muted-foreground">({supp.dosage})</span>
                              </div>
                              <Textarea
                                value={editNotes[`${slot}-${idx}`] || ''}
                                onChange={(e) => setEditNotes(prev => ({
                                  ...prev,
                                  [`${slot}-${idx}`]: e.target.value
                                }))}
                                placeholder={`Add a note for ${supp.name}... (e.g., "Take with my morning coffee", "Gives me energy")`}
                                className="w-full text-sm min-h-[60px]"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border/50 flex items-center justify-end gap-3 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setEditingSchedule(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={savingEdit || !editName.trim()}
                className="btn-primary"
              >
                {savingEdit ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      <GlobalFooter />
      
      {/* Onboarding Modal for first-time users */}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={handleCloseOnboarding} 
      />

      {/* Quick Re-Check modal — triggered from the drift-state safety pill */}
      <QuickRecheckModal
        open={showRecheckModal}
        driftInfo={driftDataForModal}
        onClose={() => {
          setShowRecheckModal(false);
          setDriftDataForModal(null);
        }}
        onSaved={(result) => {
          // After a successful re-check the backend updated the assessment,
          // so refresh drift + local assessments so the pill flips back to
          // the fresh green/amber state on next render.
          setMedDrift({ has_drift: false, added_meds: [], removed_meds: [] });
          setAssessments(prev => prev.map(a =>
            a.id === result.assessment_id
              ? { ...a, drug_interactions: result.drug_interactions, medications: result.medications, last_recheck_at: result.last_recheck_at }
              : a
          ));
          toast.success(
            result.interaction_count === 0
              ? 'All clear — no interactions flagged'
              : `${result.interaction_count} interaction${result.interaction_count !== 1 ? 's' : ''} flagged — review inside`
          );
        }}
        getAuthHeader={getAuthHeader}
      />
    </div>
  );
}
